import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api, errMsg } from "@/lib/api";
import { Loader2, FlaskConical } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setSent(true);
      setDevResetUrl(res.data.dev_reset_url || null);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <Card className="border shadow-none">
        <CardHeader>
          <CardTitle className="text-xl">Reset your password</CardTitle>
          <CardDescription>Enter your email and we'll send you a reset link.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" data-testid="forgot-email-input" />
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={busy} data-testid="forgot-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
            </Button>
            {sent && (
              <p className="text-sm text-muted-foreground leading-relaxed">
                If an account exists for that email, the link is on its way. It expires in 1 hour.
              </p>
            )}
            {devResetUrl && (
              <a href={devResetUrl} className="block" data-testid="forgot-dev-reset-link">
                <Button type="button" variant="outline" className="w-full rounded-xl gap-2">
                  <FlaskConical className="h-4 w-4" />
                  Open reset link (dev shortcut)
                </Button>
              </a>
            )}
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Remembered it?{" "}
            <Link to="/login" className="font-medium text-foreground underline underline-offset-4">Log in</Link>
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
